import { Injectable, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpHelper } from '../helpers/http-helper';
import { AuthenticationService } from './authentication.service';
import { StorageManagerService } from './storage-manager.service';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { SettingsService } from './settings.service';
import { IBNCC } from '../models/bncc-model';
import { IBNCCEstablishment } from '../models/bncc-establishment.model';
import { ILLProductBase } from '../models/ll-product-base.model';

@Injectable()
export class BNCCFormService {

  httpHelper: HttpHelper;

  constructor(private http: HttpClient, private authService: AuthenticationService, private storageManagerService: StorageManagerService,
    private settingsService: SettingsService) { 

    // Define an instance of the http helper class that will be used to create httpHeader for api calls
    this.httpHelper = new HttpHelper(http, authService, storageManagerService);
  }

  saveBNCCForm(bnccData: IBNCC, modifiedByUser: string, draftMode: boolean) {
    let headers = this.httpHelper.createHttpHeaders('SSDrugProducts,SSDrugProducts-BNCC');
    const settings = this.settingsService.getSettings();
    const url = `ssdpds/v1/BNCCForms/CreateBNCCForm?isDraftMode=${draftMode}`;
    headers = headers.set('modifiedByUser', modifiedByUser);

    return this.http.post(`${settings.ApiGatewayUrl}/${url}`, bnccData, { headers: headers })
        .pipe(map(this.extractData),
          catchError(this.handleErrors));
 }

 createBNCCForm(bnccData: IBNCC, modifiedByUser: string) {
    let headers = this.httpHelper.createHttpHeaders('SSDrugProducts,SSDrugProducts-BNCC');
    const settings = this.settingsService.getSettings();
    const url = `ssdpds/v1/BNCCForms/CreateInitialBNCCForm`;
    headers = headers.set('modifiedByUser', modifiedByUser);

    return this.http.post(`${settings.ApiGatewayUrl}/${url}`, bnccData, { headers: headers })
        .pipe(map(this.extractData),
          catchError(this.handleErrors));
 }

  getBNCCForm(bnccRevisionId: string): Observable<any> {
    let headers = this.httpHelper.createHttpHeaders('SSDrugProducts,SSDrugProducts-BNCC');
    const settings = this.settingsService.getSettings();
    const url = `ssdpds/v1/BNCCForms/${bnccRevisionId}`;

    return this.http.get(`${settings.ApiGatewayUrl}/${url}`, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  getRevisionHistory(bnccRevisionId: string, modifiedByUser: string): Observable<any[]> {
    let headers = this.httpHelper.createHttpHeaders('SSDrugProducts,SSDrugProducts-BNCC');
    const settings = this.settingsService.getSettings();
    const url = `ssdpds/v1/BNCCForms/BNCCVersionHistory/${bnccRevisionId}`;
    headers = headers.set('modifiedByUser', modifiedByUser);

    return this.http.get(`${settings.ApiGatewayUrl}/${url}`, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  getCompanyData(): Observable<any[]> {
    const headers = this.httpHelper.createHttpHeaders();
    const settings = this.settingsService.getSettings();
    const url = 'ssdpds/v1/CommonData/CompanyTypesByCustomerGuid';

    return this.http.get(`${settings.ApiGatewayUrl}/${url}`, { headers: headers })
      .pipe(map(this.extractData),
        catchError(this.handleErrors));
  }

  //Helper methods

  setProperties(formData: any, objectData: any) {
    for (let bnccProp in formData) {
      if (objectData.hasOwnProperty(bnccProp) && formData[bnccProp] !== null) {
        objectData[bnccProp] = formData[bnccProp];
      }
    }
  }

  // Take the form establishment fields and set them to the establishment object
  setEstablishmentData(establishments: any[], bnccData: IBNCC) {
    bnccData.establishments = {};
    establishments.forEach(est => {
      const newEstablishment = new IBNCCEstablishment();
      const newProduct = new ILLProductBase();

      newEstablishment.isManualEntry = !est.hasOwnProperty('versionNumber');
      newEstablishment.bnccRevisionId = bnccData.bnccRevisionId;
      newProduct.ndcProductCode = est.ndcProductCode;

      if (newEstablishment.isManualEntry) {
        newEstablishment.establishmentDUNS = est.dunsNumber;
        newEstablishment.establishmentName = est.establishmentName;
      } else {
        newEstablishment.establishmentDUNS = est.dunsNumber;
        newEstablishment.establishmentName = est.establishmentName;

        newProduct.productType = est.productType;
        newProduct.proprietaryName = est.productName
        newProduct.llModifiedOn = est.modifiedDate;
        newProduct.llFormVersion = est.versionNumber;
        newProduct.llFormName = est.formName;
        newProduct.productId = est.productId;

        // Need to convert back to Enum format, opposite of what's happening in formatCertificationStatus
        newProduct.listingRecordCertifiedThroughStatus = est.certificationStatus.toUpperCase().replace(' ', '_');
        newProduct.listingRecordCertifiedThroughDate = est.certificationDate;
        newProduct.marketingCategory = est.marketingCategory;
        newProduct.marketingStartDate = est.marketingStartDate;
        newProduct.marketingEndDate = est.marketingEndDate;
      }

      newEstablishment.product = newProduct;
      this.addEstablishmentToBNCCData(newEstablishment, bnccData);
    });
  }

  private addEstablishmentToBNCCData(establishment: IBNCCEstablishment, bnccData: IBNCC) {
    const dictionaryKey = `${establishment.establishmentDUNS} (${establishment.establishmentName})`;
    let establishments = bnccData.establishments[dictionaryKey];
    if (establishments === undefined) {
      establishments = [];
    }
    establishments.push(establishment);
    bnccData.establishments[dictionaryKey] = establishments;
  }

  private extractData(response: Response) {
    if (response.status < 200 || response.status >= 300) {
        throw new Error('Bad response status: ' + response.status);
    }

    const body = response;
    return body || {};
 }

    // Error handling
  private handleErrors(error: any): Observable<any> {
    const GENERIC_ERROR_MSG = 'It looks like something went wrong. Try reloading the page or contacting support if this issue continues.';
    const bDisplayDetailMsg = error && error.error && error.error.isBusinessErrorMessage ? true : isDevMode() ? true : false;
    error.error = error && error.error && error.error.message && bDisplayDetailMsg ? error.error.message : GENERIC_ERROR_MSG;

    return throwError(error.error);
  }
}
